import { v4 as uuidv4 } from "uuid";

export const DEFAULT_TICKET_SET_KEY = "all-dates";
export const DEFAULT_TICKET_SET_NAME = "All Dates";
export const NEW_TICKET_SET_NAME = "New Ticket Set";
export const MAX_TICKET_SETS = 20;
export const MAX_TICKET_SET_NAME_LENGTH = 50;
export const MAX_ADJUSTMENTS = 20;
export const MAX_ADJUSTMENT_PERCENT = 1000;
export const MAX_ADJUSTMENT_AMOUNT = 999999.99;

export const TICKET_SET_TIERS = ["dates", "monthly", "days", "all"];

export const APPLIES_TO_OPTIONS = [
  { value: "all", label: "All other dates" },
  { value: "days", label: "Days of the week" },
  { value: "monthly", label: "Monthly pattern" },
  { value: "dates", label: "Specific dates" },
];

export const TIME_MODE_OPTIONS = [
  { value: "all", label: "All entry times" },
  { value: "some", label: "Only some entry times" },
];

export const MONTHLY_NTH_OPTIONS = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: -1, label: "Last" },
];

export const ADJUSTMENT_KINDS = [
  { value: "less_amt", label: "Decrease by $" },
  { value: "less_pct", label: "Decrease by %" },
  { value: "more_amt", label: "Increase by $" },
  { value: "more_pct", label: "Increase by %" },
];

export const TICKET_SET_COLORS = ["brand", "purple", "orange", "pink", "indigo", "success"];

export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const dayShort = (day) => DAY_SHORT[day];
export const dayLong = (day) => DAY_LONG[day];

const atNoon = (isoDate) => new Date(`${isoDate}T12:00:00`);

export const formatEntryTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHour}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
};

const shortDate = (isoDate) => atNoon(isoDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export const longDate = (isoDate) =>
  atNoon(isoDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const sorted = (values) => [...values].sort();

export const blankTicketSet = ({ key, ...fields } = {}) => ({
  key: key || `set-${uuidv4().slice(0, 8)}`,
  name: NEW_TICKET_SET_NAME,
  appliesTo: "all",
  days: [],
  dates: [],
  monthlyNth: 1,
  monthlyDow: 4,
  timeMode: "all",
  times: [],
  ageRestriction: "",
  priceAdjustments: [],
  winsOver: [],
  ...fields,
});

export const defaultTicketSet = () => blankTicketSet({ key: DEFAULT_TICKET_SET_KEY, name: DEFAULT_TICKET_SET_NAME });

export const ticketSetsOf = (config) => {
  const saved = (config && config.ticketSets) || [];
  return saved.length > 0 ? saved.map((set, index) => blankTicketSet({ ...set, key: set.key || `set-${index + 1}` })) : [defaultTicketSet()];
};

export const ticketSetKeyOf = (type, sets) =>
  type.ticketSetKey && sets.some((set) => set.key === type.ticketSetKey) ? type.ticketSetKey : sets[0].key;

export const typesInSet = (ticketTypes, sets, key) => (ticketTypes || []).filter((type) => ticketSetKeyOf(type, sets) === key);

export const nextTicketSetName = (sets) => {
  const taken = new Set(sets.map((set) => (set.name || "").trim().toLowerCase()));
  if (!taken.has(NEW_TICKET_SET_NAME.toLowerCase())) return NEW_TICKET_SET_NAME;
  let copy = 2;
  while (taken.has(`${NEW_TICKET_SET_NAME} ${copy}`.toLowerCase())) copy += 1;
  return `${NEW_TICKET_SET_NAME} ${copy}`;
};

export const newTicketSet = (sets) => blankTicketSet({ name: nextTicketSetName(sets), appliesTo: "days" });

export const ticketSetNameProblem = (name, sets, key) => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length > MAX_TICKET_SET_NAME_LENGTH) return `Name can be at most ${MAX_TICKET_SET_NAME_LENGTH} characters`;
  const taken = trimmed.toLowerCase();
  if (sets.some((set) => set.key !== key && (set.name || "").trim().toLowerCase() === taken)) return "Another ticket set already uses this name";
  return null;
};

export const colorOf = (sets, key) => TICKET_SET_COLORS[Math.max(0, sets.findIndex((set) => set.key === key)) % TICKET_SET_COLORS.length];

const dateLookups = new WeakMap();
const dateLookupOf = (dates) => {
  if (!dates) return new Set();
  if (!dateLookups.has(dates)) dateLookups.set(dates, new Set(dates));
  return dateLookups.get(dates);
};

export const dateMatches = (set, isoDate) => {
  if (!set || !isoDate) return false;
  if (set.appliesTo === "dates") return dateLookupOf(set.dates).has(isoDate);
  if (set.appliesTo === "days") return (set.days || []).includes(atNoon(isoDate).getDay());
  if (set.appliesTo === "monthly") {
    const date = atNoon(isoDate);
    if (date.getDay() !== Number(set.monthlyDow)) return false;
    const nth = Number(set.monthlyNth);
    if (nth === -1) return date.getDate() + 7 > new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    return Math.ceil(date.getDate() / 7) === nth;
  }
  return true;
};

export const timeMatches = (set, time) => set.timeMode !== "some" || (set.times || []).includes(time);

const tierOf = (set) => {
  const index = TICKET_SET_TIERS.indexOf(set.appliesTo);
  return index === -1 ? TICKET_SET_TIERS.length - 1 : index;
};
const timed = (set) => set.timeMode === "some";
const sameRank = (a, b) => tierOf(a) === tierOf(b) && timed(a) === timed(b);
const outranks = (a, b) => tierOf(a) - tierOf(b) || Number(timed(b)) - Number(timed(a));
const beats = (a, b) => (a.winsOver || []).includes(b.key);

export const pairOf = (a, b) => (a < b ? [a, b] : [b, a]);

// Ruby twin for Conductor: EventSeries::TicketSet.resolve. Change both together.
export const resolveSlot = (sets, isoDate, time) => {
  const candidates = sets.filter((set) => dateMatches(set, isoDate) && timeMatches(set, time));
  if (candidates.length === 0) return { key: null, undecided: [] };

  const top = [...candidates].sort(outranks)[0];
  const group = candidates.filter((set) => sameRank(set, top) || (!timed(top) && timed(set)));
  if (group.length === 1) return { key: top.key, undecided: [] };

  const winner = group.find((set) => group.every((other) => other === set || beats(set, other)));
  if (winner) return { key: winner.key, undecided: [] };

  const pairs = group.flatMap((set, index) => group.slice(index + 1).map((other) => [set, other]));
  const open = pairs.filter(([a, b]) => !beats(a, b) && !beats(b, a));
  return { key: null, undecided: (open.length > 0 ? open : pairs).map(([a, b]) => pairOf(a.key, b.key)) };
};

export const effectiveAge = (set, seriesAge) => (set && set.ageRestriction) || seriesAge || "";

const quoted = (names) => {
  const parts = names.map((name) => `"${name}"`);
  return parts.length <= 2 ? parts.join(" and ") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};

export const coverage = (schedule, sets, seriesAge = "") => {
  const rows = schedule && schedule.mode === "timed_entry" ? schedule.generated || [] : [];
  const byKey = Object.fromEntries(sets.map((set) => [set.key, set]));
  const order = (key) => sets.findIndex((set) => set.key === key);
  const dates = {};
  const overlaps = {};
  const dateCounts = Object.fromEntries(sets.map((set) => [set.key, 0]));
  const ageConflicts = [];

  rows.forEach((row) => {
    const slots = sorted(new Set(row.times || [])).map((time) => {
      const result = resolveSlot(sets, row.date, time);
      result.undecided.forEach(([a, b]) => {
        const id = `${a}|${b}`;
        const [first, second] = order(a) <= order(b) ? [a, b] : [b, a];
        overlaps[id] = overlaps[id] || { id, a: first, b: second, dates: new Set(), times: new Set() };
        overlaps[id].dates.add(row.date);
        overlaps[id].times.add(time);
      });
      return { time, ...result };
    });

    const counts = {};
    slots.forEach((slot) => {
      if (slot.key) counts[slot.key] = (counts[slot.key] || 0) + 1;
    });
    const keys = sets.map((set) => set.key).filter((key) => counts[key]);
    const dominant = [...keys].sort((a, b) => counts[b] - counts[a] || order(a) - order(b))[0] || null;
    keys.forEach((key) => (dateCounts[key] += 1));

    const ages = new Set(keys.map((key) => effectiveAge(byKey[key], seriesAge)));
    if (ages.size > 1) ageConflicts.push({ date: row.date, keys });

    dates[row.date] = {
      slots,
      keys,
      key: dominant,
      uncovered: slots.some((slot) => !slot.key && slot.undecided.length === 0),
      undecided: slots.some((slot) => slot.undecided.length > 0),
    };
  });

  const scheduled = Object.keys(dates).sort();
  return {
    dates,
    scheduled,
    times: sorted(new Set(rows.flatMap((row) => row.times || []))),
    dateCounts,
    uncoveredDates: scheduled.filter((date) => dates[date].uncovered),
    undecidedDates: scheduled.filter((date) => dates[date].undecided),
    overlaps: Object.values(overlaps)
      .map((overlap) => ({ ...overlap, dates: sorted(overlap.dates), times: sorted(overlap.times) }))
      .sort((x, y) => order(x.a) - order(y.a) || order(x.b) - order(y.b)),
    ageConflicts,
  };
};

export const slotKeyFor = (cover, isoDate, time) => {
  const day = cover.dates[isoDate];
  const slot = day && day.slots.find((candidate) => candidate.time === time);
  return slot ? slot.key : null;
};

export const decideOverlap = (sets, winnerKey, loserKey) =>
  sets.map((set) => {
    if (set.key === winnerKey) return { ...set, winsOver: sorted(new Set([...(set.winsOver || []), loserKey])) };
    if (set.key === loserKey) return { ...set, winsOver: (set.winsOver || []).filter((key) => key !== winnerKey) };
    return set;
  });

export const clearDecision = (sets, winnerKey, loserKey) =>
  sets.map((set) => (set.key === winnerKey ? { ...set, winsOver: (set.winsOver || []).filter((key) => key !== loserKey) } : set));

export const decisionsOf = (sets) =>
  sets.flatMap((set) =>
    (set.winsOver || [])
      .filter((key) => key !== set.key && sets.some((other) => other.key === key))
      .map((loser) => ({ winner: set.key, loser })),
  );

export const withoutTicketSet = (sets, key) =>
  sets.filter((set) => set.key !== key).map((set) => ({ ...set, winsOver: (set.winsOver || []).filter((other) => other !== key) }));

const amountOf = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const money = (value) => Math.round(amountOf(value) * 100) / 100;

const percentKind = (kind) => (kind || "").endsWith("pct");

export const adjustmentLimit = (kind) => (percentKind(kind) ? MAX_ADJUSTMENT_PERCENT : MAX_ADJUSTMENT_AMOUNT);

export const adjustmentAmountProblem = (adjustment) => {
  if (money(adjustment.value) <= adjustmentLimit(adjustment.kind)) return null;
  return percentKind(adjustment.kind)
    ? `Amount can be at most ${MAX_ADJUSTMENT_PERCENT}%`
    : `Amount can be at most $${MAX_ADJUSTMENT_AMOUNT.toLocaleString("en-US")}`;
};

export const usableAdjustment = (adjustment) => money(adjustment.value) > 0 && !adjustmentAmountProblem(adjustment);

export const adjustmentFor = (set, time) =>
  (set && time && (set.priceAdjustments || []).find((adjustment) => (adjustment.times || []).includes(time) && usableAdjustment(adjustment))) ||
  null;

export const adjustPrice = (price, adjustment) => {
  const amount = amountOf(price);
  if (!adjustment || amount <= 0) return amount;
  const value = amountOf(adjustment.value);
  const adjusted = {
    less_amt: amount - value,
    less_pct: amount * (1 - value / 100),
    more_amt: amount + value,
    more_pct: amount * (1 + value / 100),
  }[adjustment.kind];
  if (adjusted === undefined) return amount;
  return Math.max(0, Math.round(Number((adjusted * 100).toFixed(6))) / 100);
};

export const formatMoney = (amount) => {
  const value = Number(amount) || 0;
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

export const describeAdjustment = (adjustment) => {
  const value = amountOf(adjustment.value);
  const sign = (adjustment.kind || "").startsWith("less") ? "−" : "+";
  return (adjustment.kind || "").endsWith("pct") ? `${sign}${value}%` : `${sign}$${value}`;
};

export const formatTimes = (times) => sorted(times || []).map(formatEntryTime).join(", ");

export const describeRule = (set) => {
  let base;
  if (set.appliesTo === "days") {
    base = (set.days || []).length === 0 ? "No days selected" : WEEK_ORDER.filter((day) => set.days.includes(day)).map(dayShort).join(", ");
  } else if (set.appliesTo === "monthly") {
    const nth = (MONTHLY_NTH_OPTIONS.find((option) => option.value === Number(set.monthlyNth)) || MONTHLY_NTH_OPTIONS[0]).label;
    base = `${nth} ${dayLong(Number(set.monthlyDow))} of each month`;
  } else if (set.appliesTo === "dates") {
    const dates = sorted(set.dates || []);
    if (dates.length === 0) base = "No dates selected";
    else if (dates.length <= 3) base = dates.map((date) => atNoon(date).toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(", ");
    else base = `${dates.length} specific dates`;
  } else {
    base = "All other dates";
  }
  if (set.timeMode === "some") base += ` · ${(set.times || []).length ? formatTimes(set.times) : "No entry times selected"}`;
  return base;
};

const plural = (count, one, many) => (count === 1 ? one : many);

export const ticketSetProblems = ({ sets, ticketTypes, cover }) => {
  const byKey = Object.fromEntries(sets.map((set) => [set.key, set]));
  const problems = [];
  sets.forEach((set) => {
    const label = set.name;
    if (typesInSet(ticketTypes, sets, set.key).length === 0) problems.push(`"${label}" needs at least one ticket type`);
    if (cover.scheduled.length === 0) return;
    if (set.appliesTo === "days" && (set.days || []).length === 0) problems.push(`"${label}" needs at least one day of the week`);
    if (set.appliesTo === "dates" && (set.dates || []).length === 0) problems.push(`"${label}" needs at least one date`);
    if (set.timeMode === "some" && (set.times || []).length === 0) problems.push(`"${label}" needs at least one entry time`);
    (set.priceAdjustments || []).forEach((adjustment, index) => {
      if (!(adjustment.times || []).length || !(money(adjustment.value) > 0)) {
        problems.push(`"${label}" price adjustment ${index + 1} needs entry times and an amount`);
      } else if (adjustmentAmountProblem(adjustment)) {
        problems.push(`"${label}" price adjustment ${index + 1}: ${adjustmentAmountProblem(adjustment)}`);
      }
    });
  });

  const uncovered = cover.uncoveredDates.length;
  if (uncovered > 0) problems.push(`${uncovered} ${plural(uncovered, "date has", "dates have")} entry times not covered by a ticket set`);
  const overlaps = cover.overlaps.length;
  if (overlaps > 0) problems.push(`${overlaps} ticket set ${plural(overlaps, "overlap needs", "overlaps need")} a decision`);

  const conflicts = {};
  cover.ageConflicts.forEach(({ date, keys }) => {
    const id = keys.join("|");
    conflicts[id] = conflicts[id] || { keys, dates: [] };
    conflicts[id].dates.push(date);
  });
  Object.values(conflicts).forEach(({ keys, dates }) => {
    const names = quoted(keys.map((key) => byKey[key].name));
    const when = dates.length === 1 ? shortDate(dates[0]) : `${dates.length} dates, starting ${shortDate(dates[0])}`;
    problems.push(`${names} set different ages on ${when}`);
  });
  return problems;
};

export const ticketSetRows = (schedule, config) => {
  if (schedule.mode !== "timed_entry") return [];
  const sets = ticketSetsOf(config);
  const keys = sets.map((set) => set.key);
  return sets.map((set, position) => ({
    key: set.key,
    name: (set.name || "").trim(),
    position,
    applies_to: set.appliesTo,
    days: set.appliesTo === "days" ? sorted(set.days || []).map(Number) : [],
    dates: set.appliesTo === "dates" ? sorted(set.dates || []) : [],
    monthly_nth: Number(set.monthlyNth ?? blankTicketSet().monthlyNth),
    monthly_dow: Number(set.monthlyDow ?? blankTicketSet().monthlyDow),
    time_mode: set.timeMode === "some" ? "some" : "all",
    times: set.timeMode === "some" ? sorted(set.times || []) : [],
    age_restriction: set.ageRestriction || "",
    price_adjustments: (set.priceAdjustments || [])
      .filter((adjustment) => (adjustment.times || []).length > 0 && usableAdjustment(adjustment))
      .map((adjustment) => ({
        label: (adjustment.label || "").trim(),
        kind: adjustment.kind,
        value: money(adjustment.value),
        times: sorted(adjustment.times || []),
      })),
    wins_over: (set.winsOver || []).filter((key) => key !== set.key && keys.includes(key)),
  }));
};

// The series' saved sets (ticketSetRows' shape) back in the editor's shape.
export const ticketSetsFromRows = (rows) =>
  (rows || []).map((row) => blankTicketSet({
    key: row.key,
    name: row.name,
    appliesTo: row.applies_to,
    days: row.days || [],
    dates: row.dates || [],
    monthlyNth: row.monthly_nth ?? blankTicketSet().monthlyNth,
    monthlyDow: row.monthly_dow ?? blankTicketSet().monthlyDow,
    timeMode: row.time_mode || "all",
    times: row.times || [],
    ageRestriction: row.age_restriction || "",
    winsOver: row.wins_over || [],
  }));

// The set each entry time on new dates uses, by the editor's own rules, so
// Add Dates never re-implements them: keys[date][time], and each date's main set.
export const ticketSetKeysFor = (dates, times, sets) => {
  const cover = coverage({ mode: "timed_entry", generated: dates.map((date) => ({ date, times })) }, sets);
  const keys = Object.fromEntries(dates.map((date) => [date, Object.fromEntries(times.map((time) => [time, slotKeyFor(cover, date, time)]))]));
  const byDate = Object.fromEntries(dates.map((date) => [date, (cover.dates[date] || {}).key || null]));
  // A date with an entry time no set picks: two sets claim it undecided, or none covers it.
  const unpicked = (date) => ((cover.dates[date] || {}).slots || []).filter((slot) => !slot.key);
  const undecided = dates.filter((date) => unpicked(date).some((slot) => slot.undecided.length > 0));
  const uncovered = dates.filter((date) => !undecided.includes(date) && Object.values(keys[date]).some((key) => key == null));
  return { keys, byDate, undecided, uncovered };
};
